import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import useVisibilitySensor from '@rooks/use-visibility-sensor';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CatalogPlaceBidButton } from '@/components/GridItemCard/Card/CardOptions/CatalogItemCard/components/CatalogPlaceBidButton/CatalogPlaceBidButton';
import ItemCardBadge from '@/components/GridItemCard/Card/components/ItemCardBadge/ItemCardBadge';
import ItemCardEstimate from '@/components/GridItemCard/Card/components/ItemCardEstimate/ItemCardEstimate';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import ItemCardTitle from '@/components/GridItemCard/Card/components/ItemCardTitle/ItemCardTitle';
import { ItemCardLiveButton } from '@/components/ItemCard/components/ItemCardLiveButton';
import { ImageRow } from '@/components/ItemCard/ImageRow';
import { PriceRow } from '@/components/ItemCard/PriceRow';
import { useIsBrowsingPage } from '@/hooks/useIsBrowsingPage';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import { getLiveCatalogCurrentItemId, getLiveCatalogCurrentItemIndex } from '@/redux/modules/console';
import {
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIndex,
    getItemSummaryIsCatalogOnly,
    getItemSummaryPromoted,
    getItemSummarySaleStartTs,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemCardNavigationTrigger } from '@/utils/amplitude/analytics';
import { useItemCardNavigation } from '@/utils/amplitude/experiments/hooks/useItemCardNavigation';
import { shouldShowLive } from '@/utils/live';

import { ItemCardComponent, ItemCardInfo } from '../../../types/types';

const CatalogItemCard: ItemCardComponent = ({ className, dimension, itemId, trackVisible }) => {
    const [linkHover, setLinkHover] = useState(false);

    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));

    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const showLive = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    const comingUp = catalogStatus === 'live' && currentItemIndex <= index;
    const isLive = comingUp && showLive && currentItem === itemId;
    const isLotOpen = currentItem === itemId;

    const isBrowsingPage = useIsBrowsingPage();
    const isMobile = useIsMobileLayout();

    const { onNavigateInitiated } = useItemCardNavigation(itemId);

    const cardRef = useRef(null);
    const [hasVisibilityBeenTracked, setHasVisibilityBeenTracked] = useState(false);
    const { isVisible } = useVisibilitySensor(cardRef, {
        intervalCheck: false,
        resizeCheck: true,
        scrollCheck: true,
    });

    useEffect(() => {
        if (isVisible && promoted && !hasVisibilityBeenTracked) {
            trackVisible?.(itemId, promoted);
            setHasVisibilityBeenTracked(true);
        }
    }, [hasVisibilityBeenTracked, isVisible, itemId, promoted, trackVisible]);

    const enterLinkHoverState = useCallback(() => {
        setLinkHover(true);
    }, []);

    const leaveLinkHoverState = useCallback(() => {
        setLinkHover(false);
    }, []);

    return (
        <div
            className={cn(
                "relative grid w-[inherit] justify-self-center overflow-hidden transition-all duration-[150ms] [grid-template-areas:'img'_'live'_'title'_'estimate'_'price'] [grid-template-rows:min-content_min-content_min-content_min-content_min-content]",
                isLotOpen && 'min-[991px]:bg-surface-100 min-[991px]:rounded-[3px]',
                !title &&
                    '[&_div]:!text-transparent [&_h4]:!text-transparent [&_img]:blur-[5px] [&_span]:!text-transparent',
                className
            )}
            data-testid={`catalog-item-card-${itemId}`}
            key={`${itemId}-noWidth-$`}
            ref={cardRef}
            style={{
                gridTemplateColumns: dimension ? `${dimension}px` : '1fr',
            }}
        >
            <div className="absolute right-[9px] top-[9px] z-[2]">
                <ItemCardSaveIcon
                    itemId={itemId}
                    labeled={!isMobile && isBrowsingPage}
                />
            </div>
            <ItemCardBadge
                allowBidding={isLive}
                itemId={itemId}
                savedItemsCard
            />

            <div
                className="relative max-w-full [grid-area:img]"
                onMouseEnter={enterLinkHoverState}
                onMouseLeave={leaveLinkHoverState}
            >
                <ImageRow
                    isCatalogPage
                    itemId={itemId}
                    onNavigate={() => {
                        onNavigateInitiated(ItemCardNavigationTrigger.ImageClick);
                    }}
                    showBidStatus
                    showImageWithHover={!isMobile}
                />
            </div>
            <div className="[grid-area:live]">
                <ItemCardLiveButton
                    allowBidding
                    isCatalogPage
                    itemId={itemId}
                    showDate={false}
                />
            </div>
            <div className={cn('flex items-center [grid-area:title]', !comingUp && !isLive && 'mt-xs')}>
                <ItemCardTitle
                    isHovered={linkHover}
                    itemId={itemId}
                    lineCount={2}
                    onMouseEnter={enterLinkHoverState}
                    onMouseLeave={leaveLinkHoverState}
                    onNavigate={() => {
                        onNavigateInitiated(ItemCardNavigationTrigger.TitleClick);
                    }}
                    showLotNumber
                />
            </div>
            <div className="pt-2xs [grid-area:estimate]">
                <ItemCardEstimate itemId={itemId} />
            </div>
            <div className="[grid-area:price]">
                <PriceRow
                    isCatalogCard
                    itemId={itemId}
                    onNavigate={() => {
                        onNavigateInitiated(ItemCardNavigationTrigger.PriceClick);
                    }}
                />
            </div>
            <CatalogPlaceBidButton itemId={itemId} />
        </div>
    );
};

const info: ItemCardInfo = { ItemCardComponent: CatalogItemCard };

export default info;
